import { useMemo } from 'react';
import AppGrid from '../../components/grid/AppGrid';
import type { AppGridProps } from '../../components/grid/types';
import type { CardColumn } from './cardColumns';
import './CardGrid.css';

export type { CardColumn } from './cardColumns';

/**
 * AppGrid for small lists that must fit their box: the dashboard cards and the
 * schedule run history.
 *
 * Plain AppGrid shares spare width among every column without a `width`, so
 * short columns (Unit, Room, Date, Deleted) and the Name column would grow
 * together; at the X-Large size the short ones were cut off instead. Here
 * every column that declares no `sizing` (and no `width`) is `'content'`: as
 * wide as its header and its widest value, at the current size, and it never
 * shrinks. Columns marked `sizing: 'fill'` (the name, a diet order, a message)
 * take whatever is left; when the box is too narrow they shrink first, down to
 * their `minWidth`, with AppGrid's tooltip carrying the full text. Only below
 * that does the grid scroll sideways.
 *
 * The cards are glance lists, so they keep the look they had before the
 * 2026-10-21 grid pass made every AppGrid match DevExtreme (Jeff, 2026-10-22):
 * no header filter funnels, no alternating stripes, and a 1px header rule
 * instead of the 2px one (`card-grid-plain`). Sorting stays. Set per grid
 * here, not in AppGrid's defaults; a caller can still pass its own.
 */

export type CardGridProps<T> = Omit<AppGridProps<T>, 'columns'> & {
  columns: CardColumn<T>[];
};

export default function CardGrid<T>({ columns, className, ...rest }: CardGridProps<T>) {
  const sized = useMemo(
    () => columns.map((c) => (c.sizing || c.width != null ? c : { ...c, sizing: 'content' as const })),
    [columns],
  );

  return (
    <div className="card-grid">
      <AppGrid<T>
        headerFilter={{ visible: false }}
        rowAlternationEnabled={false}
        {...rest}
        className={className ? `card-grid-plain ${className}` : 'card-grid-plain'}
        columns={sized}
      />
    </div>
  );
}
